import { QualificationBadge } from 'components/badges';
import { ErrorHandler } from 'components/errors/ErrorHandler';
import { FormattedUnitValue, TrendValue } from 'components/text';
import { useBusinessData } from 'features/dashboard/hooks';
import { AutoshipsHeader } from 'features/dashboard/widgets/firstline/headers/AutoshipHeaders';
import { CurrentQualificationHeader } from 'features/dashboard/widgets/firstline/headers/QualificationHeaders';
import {
  CustomerRegistrationsHeader,
  MyCustomerRegistrationsHeader,
  MyTPRegistrationsHeader,
  QuickstartRegistrationsHeader,
  TPRegistrationsHeader
} from 'features/dashboard/widgets/firstline/headers/RegistrationHeaders';
import {
  PersonalSalesHeader,
  SixLevelSalesHeader,
  StructureSalesHeader
} from 'features/dashboard/widgets/firstline/headers/SalesHeaders';
import { Month } from 'libs/time';
import { useTranslation } from 'libs/translations';
import _ from 'lodash';
import { FC, useMemo } from 'react';
import {
  IHistoryVolume,
  IUserQualificationHistory,
  QualificationLevel
} from 'types';
import { computePercentageTrend } from 'utils';

import { useDownlineContext } from '../DownlineContext';
import { HistoryTableSkeleton } from './HistoryTableSkeleton';

interface IHistoryTableProps {
  readonly userId: string;
}

interface MergedMonthlyData {
  date: Month;
  qualification?: QualificationLevel;
  turnoverFromStructure?: number;
  turnoverFromSixLevel?: number;
  turnoverFromSelf?: number;
  customerRegistrations?: number;
  teampartnerRegistrations?: number;
  autoshipsInStructure?: number;
  quickstarterRegistrations?: number;
  ownCustomerRegistrations?: number;
  ownTeampartnerRegistrations?: number;
}

export const HistoryTable: FC<IHistoryTableProps> = ({ userId }) => {
  const { selectedPeriod, historicalMonths } = useDownlineContext();
  const { activeLanguage } = useTranslation();

  const { data, error, isLoading } = useBusinessData({
    userId,
    historicalMonths,
    month: selectedPeriod
  });

  const dataTable = useMemo(() => {
    if (_.isUndefined(data)) {
      return null;
    }

    const map = new Map<string, Partial<MergedMonthlyData>>();

    const mergeVolume = (
      volumeRecords: {
        readonly current: number;
        readonly history: ReadonlyArray<IHistoryVolume>;
      },
      key: keyof MergedMonthlyData
    ) => {
      for (const entry of volumeRecords.history) {
        const dateKey = entry.date.toString();
        const existing = map.get(dateKey) || { date: entry.date };
        map.set(dateKey, { ...existing, [key]: entry.volume });
      }

      if (volumeRecords.current) {
        const dateKey = selectedPeriod.toString();
        const existing = map.get(dateKey) || { date: selectedPeriod };
        map.set(dateKey, {
          ...existing,
          [key]: volumeRecords.current
        });
      }
    };

    const mergeQualification = (
      entries: ReadonlyArray<IUserQualificationHistory>,
      key: keyof MergedMonthlyData
    ) => {
      for (const entry of entries) {
        const dateKey = entry.date.toString();
        const existing = map.get(dateKey) || { date: entry.date };
        map.set(dateKey, { ...existing, [key]: entry.qualification });
      }
    };

    mergeQualification(data.qualificationHistory, 'qualification');
    mergeVolume(data.turnoverFromStructure, 'turnoverFromStructure');
    mergeVolume(data.turnoverFromSixLevel, 'turnoverFromSixLevel');
    mergeVolume(data.turnoverFromSelf, 'turnoverFromSelf');
    mergeVolume(data.customerRegistrations.structure, 'customerRegistrations');
    mergeVolume(
      data.teampartnerRegistrations.structure,
      'teampartnerRegistrations'
    );
    mergeVolume(data.autoshipsInStructure, 'autoshipsInStructure');

    mergeVolume(
      data.quickstarterRegistrations.structure,
      'quickstarterRegistrations'
    );

    mergeVolume(
      data.customerRegistrations.firstline,
      'ownCustomerRegistrations'
    );
    mergeVolume(
      data.teampartnerRegistrations.firstline,
      'ownTeampartnerRegistrations'
    );

    return Array.from(map.values() as IterableIterator<MergedMonthlyData>).sort(
      (a, b) => b.date.getTime() - a.date.getTime()
    );
  }, [data]);

  const renderTrendValue = (currentMonth?: number, previousMonth?: number) => {
    if (_.isUndefined(currentMonth) || _.isUndefined(previousMonth)) {
      return null;
    }

    const monthlyTrendValue = computePercentageTrend(
      currentMonth,
      previousMonth
    );

    return <TrendValue value={monthlyTrendValue} style="illustrated" />;
  };

  if (isLoading) return <HistoryTableSkeleton />;
  if (error) return <ErrorHandler err={error} />;
  if (!dataTable) return null;

  return (
    <div className="mt-8 flow-root overflow-y-auto">
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full align-middle bg-lightGray">
          <table className="min-w-full divide-y divide-slate-100 border border-slate-100">
            <thead className="bg-white">
              <tr className="divide-x divide-slate-100 uppercase overflow-hidden whitespace-nowrap text-ellipsis text-middle">
                <th scope="col" className="py-3.5 px-2 text-left"></th>

                <th className="text-md font-medium text-gray-900 p-2 text-middle">
                  <CurrentQualificationHeader iconOnly />
                </th>

                <th
                  className="text-md font-medium text-gray-900 p-2 text-middle"
                  colSpan={2}
                >
                  <AutoshipsHeader />
                </th>
                <th
                  className="text-md font-medium text-gray-900 p-2 text-middle"
                  colSpan={2}
                >
                  <StructureSalesHeader />
                </th>

                <th
                  className="text-md font-medium text-gray-900 p-2 text-middle"
                  colSpan={2}
                >
                  <SixLevelSalesHeader />
                </th>

                <th
                  className="text-md font-medium text-gray-900 p-2 text-middle"
                  colSpan={2}
                >
                  <PersonalSalesHeader />
                </th>

                <th
                  className="text-md font-medium text-gray-900 p-2 text-middle"
                  colSpan={2}
                >
                  <TPRegistrationsHeader />
                </th>

                <th
                  className="text-md font-medium text-gray-900 p-2 text-middle"
                  colSpan={2}
                >
                  <CustomerRegistrationsHeader />
                </th>

                <th
                  className="text-md font-medium text-gray-900 p-2 text-middle"
                  colSpan={2}
                >
                  <QuickstartRegistrationsHeader />
                </th>

                <th className="text-md font-medium text-gray-900 p-2 text-middle">
                  <MyTPRegistrationsHeader />
                </th>

                <th className="text-md font-medium text-gray-900 p-2 text-middle">
                  <MyCustomerRegistrationsHeader />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {dataTable.map((d, index, array) => {
                const isCurrentMonth =
                  d.date.getTime() == Month.today().getTime();
                return (
                  <tr
                    key={`${d.date.toString()}`}
                    className={`${
                      isCurrentMonth
                        ? 'bg-secondary'
                        : 'odd:bg-white even:bg-[#f6f6f7]'
                    }  divide-x divide-slate-100 whitespace-nowrap text-sm`}
                  >
                    <td className="p-2">
                      {d.date.toLocaleString(activeLanguage)}
                    </td>

                    <td className="p-2 text-center">
                      {d.qualification ? (
                        <QualificationBadge qualification={d.qualification} />
                      ) : null}
                    </td>

                    <td className="p-2 text-right text-base">
                      {d.autoshipsInStructure && (
                        <FormattedUnitValue
                          unit="count"
                          value={d.autoshipsInStructure}
                        />
                      )}
                    </td>

                    <td className="p-2">
                      {!isCurrentMonth &&
                        renderTrendValue(
                          d.autoshipsInStructure,
                          array[index + 1]?.autoshipsInStructure
                        )}
                    </td>

                    <td className="p-2 text-right text-base">
                      {d.turnoverFromStructure && (
                        <FormattedUnitValue
                          unit="points"
                          value={d.turnoverFromStructure}
                        />
                      )}
                    </td>

                    <td className="p-2">
                      {!isCurrentMonth &&
                        renderTrendValue(
                          d.turnoverFromStructure,
                          array[index + 1]?.turnoverFromStructure
                        )}
                    </td>

                    <td className="p-2 text-right text-base">
                      {d.turnoverFromSixLevel && (
                        <FormattedUnitValue
                          unit="points"
                          value={d.turnoverFromSixLevel}
                        />
                      )}
                    </td>

                    <td className="p-2">
                      {!isCurrentMonth &&
                        renderTrendValue(
                          d.turnoverFromSixLevel,
                          array[index + 1]?.turnoverFromSixLevel
                        )}
                    </td>

                    <td className="p-2 text-right text-base">
                      {d.turnoverFromSelf && (
                        <FormattedUnitValue
                          unit="points"
                          value={d.turnoverFromSelf}
                        />
                      )}
                    </td>

                    <td className="p-2">
                      {!isCurrentMonth &&
                        renderTrendValue(
                          d.turnoverFromSelf,
                          array[index + 1]?.turnoverFromSelf
                        )}
                    </td>

                    <td className="p-2 text-right text-base">
                      {d.teampartnerRegistrations && (
                        <FormattedUnitValue
                          unit="count"
                          value={d.teampartnerRegistrations}
                        />
                      )}
                    </td>

                    <td className="p-2">
                      {!isCurrentMonth &&
                        renderTrendValue(
                          d.teampartnerRegistrations,
                          array[index + 1]?.teampartnerRegistrations
                        )}
                    </td>

                    <td className="p-2 text-right text-base">
                      {d.customerRegistrations && (
                        <FormattedUnitValue
                          unit="count"
                          value={d.customerRegistrations}
                        />
                      )}
                    </td>

                    <td className="p-2">
                      {!isCurrentMonth &&
                        renderTrendValue(
                          d.customerRegistrations,
                          array[index + 1]?.customerRegistrations
                        )}
                    </td>

                    <td className="p-2 text-right text-base">
                      {d.quickstarterRegistrations && (
                        <FormattedUnitValue
                          unit="count"
                          value={d.quickstarterRegistrations}
                        />
                      )}
                    </td>

                    <td className="p-2">
                      {!isCurrentMonth &&
                        renderTrendValue(
                          d.quickstarterRegistrations,
                          array[index + 1]?.quickstarterRegistrations
                        )}
                    </td>

                    <td className="p-2 text-right text-base">
                      {d.ownTeampartnerRegistrations && (
                        <FormattedUnitValue
                          unit="count"
                          value={d.ownTeampartnerRegistrations}
                        />
                      )}
                    </td>

                    <td className="p-2 text-right text-base">
                      {d.ownCustomerRegistrations && (
                        <FormattedUnitValue
                          unit="count"
                          value={d.ownCustomerRegistrations}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
